import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

export default function ForbiddenPage() {
  const location = useLocation();
  const { user } = useAuth();
  const [visibleLines, setVisibleLines] = useState([]);

  const area = location.pathname.replace(/^\/privat\/?/, '') || 'bereich';
  const username = user?.username || 'gast';

  const LINES = [
    { delay: 0,    text: '> whoami' },
    { delay: 600,  text: `  ${username}` },
    { delay: 1300, text: `> sudo open ${area}/` },
    { delay: 2100, text: '  permission denied: fehlende Berechtigung', error: true },
    { delay: 2800, text: '> cat error.log' },
    { delay: 3500, text: '  [403] Zugriff verweigert — dir fehlen die Rechte für diesen Bereich.', error: true },
    { delay: 4200, text: '> _', cursor: true },
  ];

  useEffect(() => {
    const timers = LINES.map((line, i) =>
      setTimeout(() => setVisibleLines(prev => [...prev, i]), line.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line

  return (
    <div className="nf-root">
      <div className="nf-bg-grid" />

      <motion.div
        className="nf-inner"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="nf-hero">
          <span className="nf-code">403</span>
          <span className="nf-slash">&lt;/&gt;</span>
        </div>

        <div className="nf-terminal">
          <div className="nf-terminal-bar">
            <span className="nf-dot nf-dot-red" />
            <span className="nf-dot nf-dot-yellow" />
            <span className="nf-dot nf-dot-green" />
            <span className="nf-terminal-title">bash — access control</span>
          </div>
          <div className="nf-terminal-body">
            {LINES.map((line, i) => (
              visibleLines.includes(i) && (
                <motion.div
                  key={i}
                  className={`nf-line${line.error ? ' nf-line-error' : ''}${line.cursor ? ' nf-line-cursor' : ''}`}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {line.text}
                </motion.div>
              )
            ))}
          </div>
        </div>

        <motion.div
          className="nf-actions"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4.4, duration: 0.5 }}
        >
          <Link to="/privat" className="btn btn-fill">← Zurück zum privaten Bereich</Link>
          <Link to="/" className="btn btn-outline">Startseite</Link>
        </motion.div>
      </motion.div>

      <style>{`
        .nf-root {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 80px 24px 40px;
        }

        .nf-bg-grid {
          position: fixed;
          inset: 0;
          background-image:
            radial-gradient(circle, rgba(251,146,60,0.06) 1px, transparent 1px);
          background-size: 32px 32px;
          pointer-events: none;
          z-index: 0;
        }

        .nf-inner {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 580px;
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        .nf-hero {
          display: flex;
          align-items: baseline;
          gap: 20px;
        }

        .nf-code {
          font-family: 'Inter', sans-serif;
          font-size: clamp(80px, 18vw, 140px);
          font-weight: 800;
          line-height: 1;
          color: transparent;
          -webkit-text-stroke: 2px rgba(245,245,245,0.15);
          letter-spacing: -4px;
          user-select: none;
        }

        .nf-slash {
          font-family: 'Inter', sans-serif;
          font-size: clamp(28px, 6vw, 48px);
          font-weight: 700;
          color: var(--accent, #fb923c);
          letter-spacing: -1px;
        }

        .nf-terminal {
          background: #0d0d0d;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        }

        .nf-terminal-bar {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 14px;
          background: #161616;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .nf-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .nf-dot-red    { background: #ff5f57; }
        .nf-dot-yellow { background: #febc2e; }
        .nf-dot-green  { background: #28c840; }

        .nf-terminal-title {
          margin-left: 8px;
          font-size: 11px;
          color: rgba(255,255,255,0.35);
          font-family: 'Inter', monospace;
        }

        .nf-terminal-body {
          padding: 20px 20px 24px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-height: 180px;
        }

        .nf-line {
          font-family: 'Courier New', 'Consolas', monospace;
          font-size: 13px;
          color: rgba(245,245,245,0.75);
          line-height: 1.6;
        }

        .nf-line-error {
          color: #f87171;
        }

        .nf-line-cursor {
          color: var(--accent, #fb923c);
          animation: nf-blink 1s step-end infinite;
        }

        @keyframes nf-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }

        .nf-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        @media (max-width: 480px) {
          .nf-actions { flex-direction: column; }
          .nf-actions a { text-align: center; justify-content: center; }
        }
      `}</style>
    </div>
  );
}
